<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  reactive,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import { Virtualizer } from 'virtua/vue';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationListSectionHeader from './widgets/ConversationListSectionHeader.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import MenuItem from './widgets/conversation/contextMenu/menuItem.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import ContactAPI from 'dashboard/api/contacts';

import { usePinnedConversations } from 'dashboard/composables/usePinnedConversations';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { useLabelVisibility } from 'dashboard/composables/useLabelVisibility';

import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import {
  conversationListPageURL,
  frontendURL,
  conversationUrl,
} from '../helper/URLHelper';
import { isAWhatsAppChannel } from 'dashboard/helper/inbox';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import {
  canFetchPartitionSections,
  conversationsForReplySection,
  dispatchPartitionSection,
  shouldPartitionAllView,
} from './helpers/chatListSectionHelper';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  topicId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  // MakerHub / Smith3D company inbox-group view ('mk' | 's3'); see Sidebar.vue.
  company: { type: String, default: '' },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);
const conversationListRef = ref(null);
const virtualListRef = ref(null);

const unrepliedSectionExpanded = ref(true);
const repliedSectionExpanded = ref(false);
const allSectionLoading = reactive({
  unreplied: false,
  replied: false,
});

const ALL_SECTION_CONFIG = {
  unreplied: {
    filterKey: 'all_unreplied',
    replyState: 'unreplied',
  },
  replied: {
    filterKey: 'all_replied',
    replyState: 'replied',
  },
};

provide('contextMenuElementTarget', conversationListRef);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ALL);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const searchQuery = ref('');
const isContextMenuOpen = ref(false);

// "Start new chat" from an empty phone-number search — opens the composer
// prefilled with the number on the currently-open channel (e.g. WS-3128-S3).
const composeRef = ref(null);
const prefillPhone = ref(null);

// Pinned (per-browser) conversations float to the top of the list.
const currentAccountIdForPins = useMapGetter('getCurrentAccountId');
const { pinnedIds, pinnedSnapshots } = usePinnedConversations(
  currentAccountIdForPins.value
);
const searchIsPhoneNumber = computed(() =>
  /^\+?\d[\d\s-]{5,}$/.test((searchQuery.value || '').trim())
);
// Normalise the typed number to E.164. A leading 0 is treated as a Malaysian
// local number (+60…); anything else is taken as already international.
const toE164 = raw => {
  const digits = (raw || '').replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return digits;
  if (digits.startsWith('0')) return `+60${digits.slice(1)}`;
  return `+${digits}`;
};
// `raw` is a string when the tap handler passes the tapped number; the "Start new
// chat" button binds @click, so Vue hands it the native PointerEvent instead — fall
// back to the search box unless a real string number was passed (a bare event would
// otherwise make toE164 call .replace on a PointerEvent and throw).
const startNewChatWithNumber = raw => {
  const number = typeof raw === 'string' ? raw : searchQuery.value;
  prefillPhone.value = toE164(number);
  nextTick(() => composeRef.value?.openWithPrefill());
};
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const urgentChatsList = useMapGetter('getUrgentChats');
const pendingChatsList = useMapGetter('getPendingChats');
const groupChatsList = useMapGetter('getGroupChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
// Server-side search results — populated by the watcher on searchQuery so
// the inbox-rail search box can find conversations beyond what the lazy
// loader has pulled into memory. See `conversationSearch` Vuex module.
const serverSearchResults = useMapGetter(
  'conversationSearch/getConversationRecords'
);
// Message-content search hits (WhatsApp-style "search inside chats"). Each hit
// carries conversation_id/inbox_id/content but no contact name, so we enrich the
// owning conversation into the store (see searchQuery watcher) and render it via
// getConversationById below.
const messageSearchResults = useMapGetter(
  'conversationSearch/getMessageRecords'
);
// NOTE: the conversations store module is NOT namespaced — 'conversations/…'
// getter/action names resolve to nothing (silently). Use the root names.
const allConversationsForSearch = useMapGetter('getAllConversations');
const isSearching = ref(false);
let searchDebounceTimer = null;
const activeInbox = useMapGetter('getSelectedInbox');
// The channel to preselect in the composer's "Via:" field. The route inbox
// (conversationInbox) is empty in the Group/overview views, so fall back to the
// currently-selected inbox and then to the open conversation's own inbox — which
// for a WhatsApp group being viewed is the WhatsApp channel we want.
const selectedChat = useMapGetter('getSelectedChat');
const prefillInboxId = computed(
  () =>
    Number(props.conversationInbox) ||
    Number(activeInbox.value) ||
    Number(selectedChat.value?.inbox_id) ||
    null
);
const conversationStats = useMapGetter('conversationStats/getStats');
const needsReplyCount = useMapGetter('conversationNeedsReplyStats/getCount');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const { visibleLabels } = useLabelVisibility();
const getTopicById = useMapGetter('mhTopics/getTopicById');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const activeTopic = computed(() => getTopicById.value(props.topicId));
const topicLabelTitles = computed(() => {
  if (!props.topicId || !activeTopic.value) return [];
  const titlesById = new Map(
    visibleLabels.value.map(label => [label.id, label.title])
  );
  return [
    ...new Set(
      activeTopic.value.label_ids
        .map(labelId => titlesById.get(labelId))
        .filter(Boolean)
    ),
  ].sort((first, second) => first.localeCompare(second));
});
const topicReady = computed(
  () =>
    !props.topicId ||
    Boolean(activeTopic.value && topicLabelTitles.value.length)
);
const topicContextKey = computed(
  () => `${props.topicId}:${topicLabelTitles.value.join(',')}`
);

// Label and topic views browse everything carrying that label/topic, so they
// default to "All" statuses rather than the saved/Open default other views use.
const isLabelOrTopicView = computed(() =>
  Boolean(props.label || props.topicId)
);

// MakerHub / Smith3D company inbox-group views. `company` is 'mk' | 's3';
// we resolve it to the account's inboxes whose names are suffixed -MK / -S3
// (owner rule 2026-07-01). Company mode behaves like a single-inbox view but
// scoped to a SET of inboxes: conversationFilters.inboxId becomes the id array,
// which the backend (ConversationFinder) and the client-side assignee getters
// both accept, so the All/Urgent/Pending/Mine/Group tabs + counts work per
// company. Inboxes without an -MK/-S3 suffix (legacy WhatsApp, test inboxes)
// intentionally belong to neither view.
const companyLabel = computed(() =>
  props.company === 's3' ? 'Smith3D' : 'MakerHub'
);

const companyInboxIds = computed(() => {
  if (!props.company) return [];
  const suffix = props.company === 's3' ? /[-\s]S3$/i : /[-\s]MK$/i;
  return inboxesList.value
    .filter(inbox => suffix.test((inbox.name || '').trim()))
    .map(inbox => inbox.id);
});

// Guard against fetching before the company's inboxes resolve. On a cold load
// the inbox list is empty for a tick; without this we'd fetch with no inbox
// scope (account-wide) and the list response's meta would clobber the tab
// counts. The companyInboxIds watcher re-runs the fetch once ids materialise.
const companyReady = computed(
  () => !props.company || companyInboxIds.value.length > 0
);

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

// Pins only apply to the plain "All" list — never inside a label view, a custom
// filter/folder, a team/company view, or the Urgent/Pending/Mine tabs. Otherwise
// pinned chats leak into filtered views they don't belong to (e.g. a pinned chat
// without the label showing under a #label view).
const isDefaultAllView = computed(
  () =>
    !props.label &&
    !props.topicId &&
    !props.conversationType &&
    !props.company &&
    !hasAppliedFiltersOrActiveFolders.value &&
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
);

const pinnedConversationList = computed(() => {
  if (!isDefaultAllView.value || searchQuery.value || !pinnedIds.value.size) {
    return [];
  }
  const loaded = allConversationsForSearch.value || [];
  return [...pinnedIds.value]
    .map(
      id =>
        loaded.find(conversation => Number(conversation.id) === Number(id)) ||
        pinnedSnapshots.value[id]
    )
    .filter(Boolean)
    .filter(conversation =>
      props.conversationInbox
        ? String(conversation.inbox_id) === String(props.conversationInbox)
        : true
    );
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const badgeCountFor = (countKey, badgeKey) => {
  if (badgeKey === 'needsReplyCount') return needsReplyCount.value || 0;
  if (badgeKey) return conversationStats.value[badgeKey] || 0;
  return conversationStats.value[countKey] || 0;
};

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey, badge: badgeKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
    // A tab may show a different number in its badge than its functional `count`
    // (e.g. All lists everything but badges only the unread subset).
    badgeCount: badgeCountFor(countKey, badgeKey),
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const currentCursor = useFunctionGetter(
  'conversationPage/getNextCursor',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  ).count;
  return count;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  // Guard against tabs missing from conversationPage's initial state — an
  // undefined page made this NaN, which the API coerced back to page 1 and
  // the lazy loader looped on the same page forever (urgent tab, 2026-06-11).
  return (currentPage.value || 0) + 1;
});

const conversationLabelFilters = computed(() => {
  if (props.topicId) {
    return topicLabelTitles.value.length ? topicLabelTitles.value : undefined;
  }
  return props.label ? [props.label] : undefined;
});

const conversationFilters = computed(() => {
  return {
    // Company views scope to a SET of inboxes (array -> inbox_id[] server-side);
    // single-inbox views pass one id; everything else is account-wide.
    inboxId: props.company
      ? companyInboxIds.value
      : props.conversationInbox || undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    cursor: currentCursor.value || undefined,
    labels: conversationLabelFilters.value,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
  };
});

const needsReplyRequest = computed(() => {
  if (hasActiveFolders.value) {
    return { queryData: activeFolder.value.query };
  }
  if (hasAppliedFilters.value) {
    return {
      queryData: filterQueryGenerator(useSnakeCase(appliedFilters.value)),
    };
  }
  return {
    filters: {
      inboxId: conversationFilters.value.inboxId,
      status: conversationFilters.value.status,
      labels: conversationFilters.value.labels,
      teamId: conversationFilters.value.teamId,
      conversationType: conversationFilters.value.conversationType,
    },
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (activeTopic.value) {
    return activeTopic.value.name;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (props.company) {
    return companyLabel.value;
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  if (!topicReady.value) return [];
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'urgent') {
      localConversationList = [...urgentChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'pending') {
      localConversationList = [...pendingChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'group') {
      localConversationList = [...groupChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    // Digit view of the query for phone matching: staff habitually search the
    // LAST 4 digits of a customer's number, or paste the local 0-prefixed form
    // of the +60 E.164 we store (0127… == +60127…), so match on digits too.
    const qDigits = q.replace(/\D/g, '');
    const qDigitVariants =
      qDigits.length >= 4
        ? [
            qDigits,
            ...(qDigits.startsWith('0') ? [`60${qDigits.slice(1)}`] : []),
          ]
        : [];
    // Build a server+local merged result set, deduped by id. Local hits show
    // immediately as the user types; server results fold in once the debounced
    // /conversations/search call returns. This unblocks finding old buyers
    // (e.g. Shopee usernames) whose conversations were never lazy-loaded —
    // the previous behavior could only filter what was already in memory.
    const localFiltered = localConversationList.filter(chat => {
      const sender = chat.meta?.sender || {};
      const name = (sender.name || '').toLowerCase();
      const phone = (sender.phone_number || '').toLowerCase();
      const phoneDigits = phone.replace(/\D/g, '');
      // Store conversations expose the display_id AS `id` (jbuilder/presenter:
      // `json.id conversation.display_id`) — there is no `display_id` field on
      // API-loaded rows, only on rows this file synthesizes itself.
      const displayId = String(chat.display_id ?? chat.id ?? '');
      return (
        name.includes(q) ||
        phone.includes(q) ||
        (phoneDigits && qDigitVariants.some(v => phoneDigits.includes(v))) ||
        displayId.includes(q)
      );
    });
    const seen = new Set(localFiltered.map(c => c.id));

    // /search/conversations returns a different shape than the inbox-rail's
    // ConversationCard expects (flat `contact`/`inbox` keys, no `meta`,
    // no `messages[]`, no `display_id`). Synthesize the missing fields so
    // search-only hits render correctly. Click still routes via chat.id.
    const remoteOnly = (serverSearchResults.value || [])
      .filter(c => !seen.has(c.id))
      .map(c => ({
        id: c.id,
        display_id: c.id,
        inbox_id: c.inbox?.id,
        status: c.status || 'open',
        unread_count: 0,
        priority: c.priority || null,
        labels: c.labels || [],
        timestamp: c.message?.created_at || c.last_activity_at || c.created_at,
        created_at: c.created_at,
        additional_attributes: c.additional_attributes || {},
        custom_attributes: c.custom_attributes || {},
        meta: {
          sender: c.contact || {},
          channel: c.inbox?.channel_type,
          assignee: c.assignee || null,
          team: c.team || null,
        },
        messages: c.message ? [c.message] : [],
      }));

    // Content matches: conversations where the query appears inside a message
    // body (not the name/number). Deduped against rows already shown above.
    // Enriched conversations (fetched in the watcher) render with their real
    // name/avatar; any not yet loaded fall back to a minimal row whose preview
    // is the matched message text.
    // Message hits key on `conversation_id`, which the search API returns as the
    // conversation's display_id (not its internal id).
    const shownIds = new Set(
      [...localFiltered, ...remoteOnly].map(c => String(c.display_id ?? c.id))
    );
    const contentHitConvIds = new Set();
    const contentHits = [];
    (messageSearchResults.value || []).forEach(m => {
      const displayId = m.conversation_id;
      if (
        !displayId ||
        shownIds.has(String(displayId)) ||
        contentHitConvIds.has(displayId)
      ) {
        return;
      }
      contentHitConvIds.add(displayId);
      // The row preview must be the MATCHED message (why it surfaced), not the
      // conversation's latest message — otherwise a recent match reads as stale
      // unrelated content. ConversationCard shows the last entry in `messages`.
      const matchedMessage = {
        id: m.id || displayId,
        content: m.content || '',
        message_type: m.message_type ?? 0,
        created_at: m.created_at,
        private: false,
        content_attributes: {},
      };
      // Real message PK (not the displayId fallback) — lets the card deep-link
      // the click to the matched message (?messageId=) so the conversation
      // opens scrolled to WHY it matched, WhatsApp-style.
      const searchMessageId = m.id || null;
      // Prefer the fully-loaded conversation (real name/avatar) when it's in the
      // store; the watcher also fetches missing ones. But ALWAYS render the hit —
      // fall back to a row synthesized from the message itself, so content matches
      // never silently vanish just because the conversation isn't loaded yet.
      const enriched = (allConversationsForSearch.value || []).find(
        c => String(c.id) === String(displayId)
      );
      if (enriched) {
        contentHits.push({
          ...enriched,
          messages: [matchedMessage],
          last_non_activity_message: matchedMessage,
          timestamp: m.created_at,
          searchMessageId,
        });
        return;
      }
      contentHits.push({
        id: displayId,
        display_id: displayId,
        inbox_id: m.inbox_id,
        status: m.status || 'open',
        unread_count: 0,
        priority: null,
        labels: [],
        timestamp: m.created_at,
        created_at: m.created_at,
        additional_attributes: {},
        custom_attributes: {},
        meta: {
          sender: m.sender || {},
          channel: null,
          assignee: null,
          team: null,
        },
        messages: [matchedMessage],
        searchMessageId,
      });
    });

    // Contact (name/number) matches always rank above message-content hits.
    // Within the contact section, float rows whose phone ENDS with the typed
    // digits — the "search by last 4 digits" habit — regardless of whether the
    // row came from the local list or the server search. Stable sort keeps the
    // existing (recency) order inside each rank.
    const contactMatches = [...localFiltered, ...remoteOnly];
    if (qDigitVariants.length) {
      const phoneTailRank = chat => {
        const digits = (chat.meta?.sender?.phone_number || '').replace(
          /\D/g,
          ''
        );
        return digits && qDigitVariants.some(v => digits.endsWith(v)) ? 0 : 1;
      };
      contactMatches.sort((a, b) => phoneTailRank(a) - phoneTailRank(b));
    }
    localConversationList = [...contactMatches, ...contentHits];

    // When an inbox is open, keep search results within that channel — the
    // server-side name/number and content searches are account-wide, so drop
    // hits from other inboxes.
    if (props.conversationInbox) {
      localConversationList = localConversationList.filter(
        c => String(c.inbox_id) === String(props.conversationInbox)
      );
    }
  }

  // Pins are rendered in their own labeled section. Remove them from the normal
  // stream so the All contract remains exact: needs-reply first, then replied,
  // with recency inside each partition.
  if (!searchQuery.value && isDefaultAllView.value && pinnedIds.value.size) {
    localConversationList = localConversationList.filter(
      conversation => !pinnedIds.value.has(Number(conversation.id))
    );
  }

  if (props.topicId) {
    localConversationList = localConversationList.filter(conversation =>
      topicLabelTitles.value.some(title =>
        (conversation.labels || []).includes(title)
      )
    );
  }

  return localConversationList;
});

// Sections (Unreplied/Replied) apply wherever the full "All" stream renders:
// the account-wide default view, single-inbox views, AND the MakerHub/Smith3D
// company views, AND topic views — same All tab, no plain label/type/folder
// filters, no search. Company views inherit inbox_id[]; topic views inherit
// their resolved label-title array. Counts follow the same scopes through the
// deep needsReplyRequest watcher. Pins deliberately stay gated on
// isDefaultAllView (account-wide only), so scoped views render sections without
// a pinned block.
const isPartitionedAllView = computed(() =>
  shouldPartitionAllView({
    label: props.label,
    conversationType: props.conversationType,
    hasAppliedFiltersOrActiveFolders: hasAppliedFiltersOrActiveFolders.value,
    assigneeType: activeAssigneeTab.value,
    allAssigneeType: wootConstants.ASSIGNEE_TYPE.ALL,
    searchQuery: searchQuery.value,
  })
);
const partitionSectionsReady = computed(() =>
  canFetchPartitionSections({
    isPartitionedAllView: isPartitionedAllView.value,
    companyReady: companyReady.value,
    topicReady: topicReady.value,
  })
);
// Literal semantics (owner 2026-07-11): mh_unreplied = the last visible
// message is the customer's (stickers/photos/templates we sent count as
// replies here). The strict radar flag (mh_needs_reply) still rides the same
// rows for the Urgent tab — do NOT bucket sections on it.
const unrepliedConversationList = computed(() =>
  conversationsForReplySection(conversationList.value, 'unreplied')
);
const repliedConversationList = computed(() =>
  conversationsForReplySection(conversationList.value, 'replied')
);
const unrepliedSectionCount = computed(() => needsReplyCount.value || 0);
const repliedSectionCount = computed(() =>
  Math.max(
    (conversationStats.value.allCount || 0) - unrepliedSectionCount.value,
    0
  )
);
const isAnyAllSectionLoading = computed(
  () => allSectionLoading.unreplied || allSectionLoading.replied
);
const unrepliedSectionEndReached = computed(() =>
  store.getters['conversationPage/getHasEndReached']('all_unreplied')
);
const repliedSectionEndReached = computed(() =>
  store.getters['conversationPage/getHasEndReached']('all_replied')
);
const isListLoading = computed(
  () => chatListLoading.value || isAnyAllSectionLoading.value
);

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = isLabelOrTopicView.value
    ? wootConstants.STATUS_TYPE.ALL
    : status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
      cursor: currentCursor.value || undefined,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
      cursor: currentCursor.value || undefined,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: visibleLabels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

// Background convergence for autonomous events (unattributed broadcasts, bulk
// clears observed from other tabs, drift, returning to a hidden tab): walk the
// Unreplied section quietly, merge rows in place, and row-refetch anything
// still rendered as Unreplied that the server no longer lists. The list never
// empties and the scroll never jumps — autonomous events update rows and the
// badge, never the page (owner rule, 2026-07-11). resetAndFetchData stays
// reserved for user-initiated context switches.
let sectionSyncInFlight = false;
let sectionSyncRearm = false;
// Signature of the rows the previous pass failed to converge (e.g. deleted
// conversations whose refetch 404s silently). An identical remainder on the
// next pass stops re-arming instead of re-requesting the same rows every
// second — the next organic trigger or user navigation retries.
let sectionSyncStuckKey = '';
async function syncSectionsQuietly() {
  if (!partitionSectionsReady.value) return;
  // Coalesce instead of dropping: a trigger landing mid-sync (or while a
  // normal section fetch is loading) re-arms exactly one trailing pass, so
  // events after the in-flight snapshot still converge. Each pass makes
  // progress, so re-arming terminates.
  if (
    sectionSyncInFlight ||
    allSectionLoading.unreplied ||
    allSectionLoading.replied
  ) {
    sectionSyncRearm = true;
    return;
  }
  sectionSyncInFlight = true;
  try {
    const fetchedIds = new Set();
    let cursor;
    let sawEnd = false;
    // The Unreplied section is small by design (~35 rows); cap the walk
    // anyway. Page 1 merges (refreshes what's on screen + newest arrivals);
    // deeper pages are ids-only probes so a background walk never inserts
    // dozens of not-yet-paginated rows above the viewport.
    for (let page = 1; page <= 3; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const data = await dispatchPartitionSection({
        dispatch: store.dispatch,
        ready: partitionSectionsReady.value,
        filterKey: 'all_unreplied',
        conversationFilters: conversationFilters.value,
        labels: conversationLabelFilters.value,
        page,
        cursor,
        replyState: 'unreplied',
        sectionOptions: {
          quiet: page === 1,
          probe: page > 1,
        },
      });
      if (!data) return;
      // A user-facing section fetch started while we awaited — yield to it
      // (its unconditional commits must not race a stale walk snapshot) and
      // re-arm a trailing pass from its finally.
      if (allSectionLoading.unreplied || allSectionLoading.replied) {
        sectionSyncRearm = true;
        return;
      }
      (data.payload || []).forEach(row => fetchedIds.add(Number(row.id)));
      cursor = data.meta?.next_cursor;
      if (!cursor) {
        sawEnd = true;
        break;
      }
    }
    if (sawEnd) {
      // Full membership known: rows still rendered as Unreplied but absent
      // from the server's set changed state without a row event (bulk clears,
      // raw imports) — refetch those few for their exact new projection.
      // Awaited so a re-armed pass sees their outcome; an identical stuck
      // remainder stops re-arming (no request loop under degradation); a
      // remainder beyond the per-pass cap re-arms a trailing pass.
      const departed = unrepliedConversationList.value.filter(
        row => !fetchedIds.has(Number(row.id))
      );
      const departedKey = departed
        .map(row => Number(row.id))
        .sort((a, b) => a - b)
        .join(',');
      if (departedKey && departedKey === sectionSyncStuckKey) {
        sectionSyncRearm = false;
      } else if (departed.length) {
        await Promise.all(
          departed
            .slice(0, 25)
            .map(row => store.dispatch('getConversation', row.id))
        );
        if (departed.length > 25) sectionSyncRearm = true;
      }
      sectionSyncStuckKey = departedKey;
    }
    if (repliedSectionExpanded.value) {
      await dispatchPartitionSection({
        dispatch: store.dispatch,
        ready: partitionSectionsReady.value,
        filterKey: 'all_replied',
        conversationFilters: conversationFilters.value,
        labels: conversationLabelFilters.value,
        page: 1,
        replyState: 'replied',
        sectionOptions: { quiet: true },
      });
    }
  } finally {
    sectionSyncInFlight = false;
    if (sectionSyncRearm) {
      sectionSyncRearm = false;
      setTimeout(() => syncSectionsQuietly(), 1000);
    }
  }
}

// Fired from fetchAllSection's finally — a re-arm set while a normal section
// fetch was loading must run once that lane frees, or a trigger during a
// dead-quiet period would never converge.
function fireSectionSyncRearm() {
  if (!sectionSyncRearm || sectionSyncInFlight) return;
  sectionSyncRearm = false;
  setTimeout(() => syncSectionsQuietly(), 1000);
}

async function fetchAllSection(section) {
  // Guard here, not only in fetchConversations: the section sentinel's
  // IntersectionObserver and the header toggle call this directly. Company
  // views must wait for inbox ids; topic views must wait for resolved label
  // titles. Either missing scope would serialize as an account-wide fetch.
  if (!partitionSectionsReady.value) return;
  const config = ALL_SECTION_CONFIG[section];
  if (!config || allSectionLoading[section]) return;

  const hasEnded = store.getters['conversationPage/getHasEndReached'](
    config.filterKey
  );
  if (hasEnded) return;

  allSectionLoading[section] = true;
  try {
    const page =
      (store.getters['conversationPage/getCurrentPageFilter'](
        config.filterKey
      ) || 0) + 1;
    const cursor = store.getters['conversationPage/getNextCursor'](
      config.filterKey
    );
    const result = await dispatchPartitionSection({
      dispatch: store.dispatch,
      ready: partitionSectionsReady.value,
      filterKey: config.filterKey,
      conversationFilters: conversationFilters.value,
      labels: conversationLabelFilters.value,
      page,
      cursor: cursor || undefined,
      replyState: config.replyState,
    });
    if (result) emitConversationLoaded();
  } finally {
    allSectionLoading[section] = false;
    fireSectionSyncRearm();
  }
}

function fetchConversations() {
  if (!companyReady.value || !topicReady.value) return;
  store.dispatch('updateChatListFilters', conversationFilters.value);
  if (isPartitionedAllView.value) {
    fetchAllSection('unreplied');
    if (repliedSectionExpanded.value) fetchAllSection('replied');
    return;
  }
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function toggleAllSection(section) {
  const expandedRef =
    section === 'unreplied' ? unrepliedSectionExpanded : repliedSectionExpanded;
  expandedRef.value = !expandedRef.value;
  if (expandedRef.value) fetchAllSection(section);
}

let needsReplyHiddenDirty = false;

function fetchNeedsReplyCount({ force = false } = {}) {
  if (!companyReady.value || !topicReady.value) return;
  if (document.visibilityState !== 'visible') {
    needsReplyHiddenDirty = true;
    return;
  }
  store.dispatch('conversationNeedsReplyStats/get', {
    ...needsReplyRequest.value,
    force,
  });
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  // In-flight section responses from the previous context are dropped by the
  // fetchEpoch bump inside conversationPage/reset; clear the loading flags so
  // the new context's section fetches aren't swallowed by the dedupe guard.
  allSectionLoading.unreplied = false;
  allSectionLoading.replied = false;
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

// MakerHub: right-click menu on the Urgent tab — bulk-clear stamps mh_handled_at
// server-side; cleared conversations re-urgent on the next customer message.
const urgentMenu = ref({ show: false, x: 0, y: 0 });

function onUrgentContextMenu({ x, y }) {
  urgentMenu.value = { show: true, x, y };
}

function closeUrgentMenu() {
  urgentMenu.value.show = false;
}

async function clearUrgent(olderThanDays) {
  closeUrgentMenu();
  try {
    const { data } = await ConversationApi.clearUrgent(
      olderThanDays ? { older_than_days: olderThanDays } : {}
    );
    useAlert(t('CHAT_LIST.URGENT_MENU.CLEARED', { count: data.cleared }));
    store.dispatch('conversationStats/get', conversationFilters.value);
    fetchNeedsReplyCount();
    if (activeAssigneeTab.value === 'urgent') resetAndFetchData();
  } catch (error) {
    useAlert(t('CHAT_LIST.URGENT_MENU.CLEAR_ERROR'));
  }
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

function loadMoreAllSection(section) {
  if (!isPartitionedAllView.value) return;
  fetchAllSection(section);
}

// Use IntersectionObserver instead of @scroll since Virtualizer only emits on user scroll.
// If the list doesn’t fill the viewport, loading can stall.
// IntersectionObserver triggers as soon as the sentinel is visible.
const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    searchQuery.value = '';
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    if (!currentPage.value) {
      fetchConversations();
    }
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, topicId, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      topicId,
      teamId,
      company: props.company,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (
    hasAppliedFiltersOrActiveFolders.value ||
    !companyReady.value ||
    !topicReady.value
  ) {
    return;
  }
  store.dispatch('conversationStats/get', conversationFilters.value);
});

useEmitter(
  'fetch_needs_reply_count',
  ({ reason, conversation_ids: conversationIds } = {}) => {
    if (document.visibilityState !== 'visible') {
      needsReplyHiddenDirty = true;
      return;
    }
    fetchNeedsReplyCount();
    if (reason === 'conversation.bulk_handled') {
      // Bulk clears change many rows with no per-row events. Quiet sync walks
      // the section and row-refetches what left it — no visible reset.
      syncSectionsQuietly();
      return;
    }
    // Reasons whose row state arrived via no message payload (raw-SQL imports
    // reconciled server-side; attachment rows committed outside their message's
    // create). When the event names its conversations, converge row-by-row —
    // the show endpoint returns truthful reply-state fields. Unattributed or
    // over-cap events degrade to the quiet section sync, never a full reset
    // (refresh-storm regression, 2026-07-11: reconcile fires ~1/min under
    // normal traffic, stickers/photos on every ack).
    if (['import.reconciled', 'attachment.changed'].includes(reason)) {
      if (Array.isArray(conversationIds)) {
        conversationIds.forEach(id => store.dispatch('getConversation', id));
      } else {
        syncSectionsQuietly();
      }
    }
  }
);

useEmitter('needs_reply_count_drift', () => {
  // A forced safety check found a count change without any observed row
  // event. Converge quietly — the badge already updated; the section walk
  // brings membership back in line without resetting the page.
  syncSectionsQuietly();
});

useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, () => {
  fetchNeedsReplyCount({ force: true });
});

// Tapping a phone number (or resolved @mention) in a WhatsApp message opens that
// number's chat: jump into the contact's existing WhatsApp conversation if there is
// one, otherwise open the composer prefilled — auto-creating the contact and landing
// on the message box (startNewChatWithNumber → ComposeConversation.openWithPrefill).
const findWhatsappConversationByPhone = async e164 => {
  const digits = e164.replace(/\D/g, '');
  if (!digits) return null;
  // Search by digits (not the +E.164 form) so the query matches the stored
  // "+60…" as a substring, then confirm an exact phone match to avoid false hits.
  let matchedContact = null;
  try {
    const {
      data: { payload = [] },
    } = await ContactAPI.search(digits);
    matchedContact = payload.find(
      c => String(c.phone_number || '').replace(/\D/g, '') === digits
    );
  } catch (error) {
    return null;
  }
  if (!matchedContact) return null;
  // NOTE: this endpoint returns only the 20 most-recent conversations (backend
  // caps it, no pagination). A contact with 20+ newer non-WhatsApp threads could
  // have its WhatsApp thread miss the window — we then fall through to the composer,
  // which still selects the existing contact (no duplicate contact, at worst a new
  // conversation). Rare (needs a merged multi-channel contact) and non-destructive.
  let conversations = [];
  try {
    const {
      data: { payload = [] },
    } = await ContactAPI.getConversations(matchedContact.id);
    conversations = payload;
  } catch (error) {
    return null;
  }
  const inboxById = id => inboxesList.value.find(item => item.id === id);
  return (
    conversations
      .filter(c => isAWhatsAppChannel(inboxById(c.inbox_id)))
      .sort(
        (a, b) => (b.last_activity_at || 0) - (a.last_activity_at || 0)
      )[0] || null
  );
};

useEmitter(BUS_EVENTS.OPEN_WA_MENTION, async phone => {
  if (!phone) return;
  const e164 = toE164(String(phone));
  const existing = await findWhatsappConversationByPhone(e164);
  if (existing) {
    router.push({
      path: frontendURL(
        conversationUrl({ accountId: currentAccountId.value, id: existing.id })
      ),
    });
    return;
  }
  startNewChatWithNumber(e164);
});

let needsReplySafetyTimer = null;
const scheduleNeedsReplySafetyRefresh = () => {
  clearTimeout(needsReplySafetyTimer);
  const jitterMs = (45 + Math.random() * 30) * 1000;
  needsReplySafetyTimer = setTimeout(() => {
    if (document.visibilityState === 'visible') {
      fetchNeedsReplyCount({ force: true });
    }
    scheduleNeedsReplySafetyRefresh();
  }, jitterMs);
};

const handleNeedsReplyVisibility = () => {
  if (document.visibilityState === 'visible') {
    if (needsReplyHiddenDirty) {
      needsReplyHiddenDirty = false;
      // Events arrived while the tab was hidden; converge without wiping
      // whatever the user left on screen.
      syncSectionsQuietly();
    }
    fetchNeedsReplyCount({ force: true });
  }
};

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  fetchNeedsReplyCount({ force: true });
  document.addEventListener('visibilitychange', handleNeedsReplyVisibility);
  scheduleNeedsReplySafetyRefresh();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

onBeforeUnmount(() => {
  clearTimeout(needsReplySafetyTimer);
  document.removeEventListener('visibilitychange', handleNeedsReplyVisibility);
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

// Entering a label/topic view forces "All" statuses; leaving restores the
// saved/Open default. Registered BEFORE the navigation watchers below so
// activeStatus is corrected before they trigger resetAndFetchData().
watch(isLabelOrTopicView, isLabelOrTopic => {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  activeStatus.value = isLabelOrTopic
    ? wootConstants.STATUS_TYPE.ALL
    : filterBy.status || wootConstants.STATUS_TYPE.OPEN;
});

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(topicContextKey, () => resetAndFetchData());
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);
watch(
  computed(() => props.company),
  () => resetAndFetchData()
);
// On a cold load straight to /company/:company the inbox list may resolve after
// the first fetch; refetch the list AND the tab counts once the company's inbox
// ids materialise (otherwise the stats stay account-wide).
watch(companyInboxIds, (newIds, oldIds) => {
  if (props.company && String(newIds) !== String(oldIds)) {
    resetAndFetchData();
    store.dispatch('conversationStats/get', conversationFilters.value);
  }
});

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});

watch(
  needsReplyRequest,
  () => {
    fetchNeedsReplyCount();
  },
  { deep: true }
);

// Debounced server-side search. Mirrors what the top-header global search
// does, but scopes the result into the inbox-rail list so operators don't
// have to switch contexts to find a buyer they already remember by name.
// The local-side filter still runs synchronously inside `conversationList`
// for instant feedback while the server call is in flight.
watch(searchQuery, newQ => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  const q = (newQ || '').trim();
  if (!q) {
    isSearching.value = false;
    store.dispatch('conversationSearch/clearSearchResults');
    return;
  }
  isSearching.value = true;
  searchDebounceTimer = setTimeout(async () => {
    try {
      // Clear stale results before the new query lands so we don't append
      // forever (the search mutations push onto an array).
      store.dispatch('conversationSearch/clearSearchResults');
      // Run name/number search and message-content search together so the
      // inbox rail can also find chats by what was said inside them. When an
      // inbox is open, scope the content search to it (the merge below also
      // filters name/number hits) so results stay within the active channel.
      const inboxId = props.conversationInbox || undefined;
      await Promise.all([
        store.dispatch('conversationSearch/conversationSearch', { q }),
        store.dispatch('conversationSearch/messageSearch', { q, inboxId }),
      ]);
      // Content hits carry no contact name — only the conversation's display_id
      // (returned as `conversation_id`). Pull each owning conversation into the
      // store so the row renders with its real name/avatar. Skip ones already
      // loaded to avoid redundant fetches.
      const loaded = store.getters.getAllConversations || [];
      // `c.id` IS the display_id on store conversations (see localFiltered note)
      const loadedDisplayIds = new Set(loaded.map(c => String(c.id)));
      const toEnrich = [
        ...new Set(
          (store.getters['conversationSearch/getMessageRecords'] || [])
            .map(m => m.conversation_id)
            .filter(
              displayId => displayId && !loadedDisplayIds.has(String(displayId))
            )
        ),
      ];
      await Promise.all(
        toEnrich.map(displayId => store.dispatch('getConversation', displayId))
      );
    } finally {
      isSearching.value = false;
    }
  }, 250);
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap bg-n-surface-1"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="isListLoading && !conversationList.length"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
      @urgent-context-menu="onUrgentContextMenu"
    />
    <ContextMenu
      v-if="urgentMenu.show"
      :x="urgentMenu.x"
      :y="urgentMenu.y"
      @close="closeUrgentMenu"
    >
      <div
        class="p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50"
      >
        <MenuItem
          :option="{
            icon: 'checkmark',
            label: t('CHAT_LIST.URGENT_MENU.CLEAR_OLDER_3D'),
          }"
          variant="icon"
          @click.stop="clearUrgent(3)"
        />
        <MenuItem
          :option="{
            icon: 'dismiss-circle',
            label: t('CHAT_LIST.URGENT_MENU.CLEAR_ALL'),
          }"
          variant="icon"
          @click.stop="clearUrgent()"
        />
      </div>
    </ContextMenu>

    <div class="px-2 pt-2 pb-1">
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="$t('CHAT_LIST.SEARCH_INPUT_PLACEHOLDER')"
        class="w-full px-2 py-1.5 text-sm border rounded-md border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
      />
    </div>

    <p
      v-if="
        !isPartitionedAllView &&
        !isListLoading &&
        !conversationList.length &&
        !searchQuery
      "
      class="flex overflow-auto justify-center items-center p-4"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <div
      v-if="searchQuery && !conversationList.length"
      class="flex flex-col gap-3 justify-center items-center p-4 text-n-slate-11 text-sm"
    >
      <span>{{ $t('CHAT_LIST.SEARCH_NO_RESULTS') }}</span>
      <button
        v-if="searchIsPhoneNumber"
        class="px-3 py-1.5 rounded-md bg-n-brand text-white text-sm font-medium"
        @click="startNewChatWithNumber"
      >
        {{ $t('CHAT_LIST.START_NEW_CHAT', { number: searchQuery.trim() }) }}
      </button>
    </div>
    <ComposeConversation
      ref="composeRef"
      is-modal
      :prefill-phone="prefillPhone"
      :prefill-inbox-id="prefillInboxId"
    />
    <ConversationBulkActions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <div
      ref="conversationListRef"
      class="flex-1 min-h-0 overflow-y-auto conversations-list"
      :class="{ '!overflow-hidden': isContextMenuOpen }"
    >
      <div
        v-if="pinnedConversationList.length"
        class="pb-1 border-b border-n-weak"
      >
        <p
          class="px-3 pt-2 pb-1 text-xxs font-medium tracking-wide uppercase text-n-slate-10"
        >
          {{ $t('CHAT_LIST.PINNED_SECTION') }}
        </p>
        <ConversationItem
          v-for="(item, index) in pinnedConversationList"
          :key="`pinned-${item.id}`"
          :source="item"
          :label="label"
          :topic-id="topicId"
          :team-id="teamId"
          :folders-id="foldersId"
          :company="company"
          :conversation-type="conversationType"
          :show-assignee="showAssigneeInConversationCard"
          :data-index="-index - 1"
          @select-conversation="selectConversation"
          @de-select-conversation="deSelectConversation"
        />
      </div>
      <div v-if="isPartitionedAllView">
        <section class="border-b border-n-weak">
          <ConversationListSectionHeader
            :title="$t('CHAT_LIST.SECTIONS.UNREPLIED')"
            :count="unrepliedSectionCount"
            :expanded="unrepliedSectionExpanded"
            danger
            @toggle="toggleAllSection('unreplied')"
          />
          <template v-if="unrepliedSectionExpanded">
            <Virtualizer
              v-slot="{ item, index }"
              :data="unrepliedConversationList"
            >
              <ConversationItem
                :source="item"
                :label="label"
                :topic-id="topicId"
                :team-id="teamId"
                :folders-id="foldersId"
                :company="company"
                :conversation-type="conversationType"
                :show-assignee="showAssigneeInConversationCard"
                :data-index="index"
                @select-conversation="selectConversation"
                @de-select-conversation="deSelectConversation"
              />
            </Virtualizer>
            <p
              v-if="!allSectionLoading.unreplied && !unrepliedSectionCount"
              class="px-3 py-4 text-xs text-center text-n-slate-10"
            >
              {{ $t('CHAT_LIST.SECTIONS.EMPTY') }}
            </p>
            <div
              v-if="allSectionLoading.unreplied"
              class="flex justify-center my-3"
            >
              <Spinner class="text-n-brand" />
            </div>
            <IntersectionObserver
              v-else-if="!unrepliedSectionEndReached"
              :options="intersectionObserverOptions"
              @observed="loadMoreAllSection('unreplied')"
            />
          </template>
        </section>

        <section>
          <ConversationListSectionHeader
            :title="$t('CHAT_LIST.SECTIONS.REPLIED')"
            :count="repliedSectionCount"
            :expanded="repliedSectionExpanded"
            @toggle="toggleAllSection('replied')"
          />
          <template v-if="repliedSectionExpanded">
            <Virtualizer
              v-slot="{ item, index }"
              :data="repliedConversationList"
            >
              <ConversationItem
                :source="item"
                :label="label"
                :topic-id="topicId"
                :team-id="teamId"
                :folders-id="foldersId"
                :company="company"
                :conversation-type="conversationType"
                :show-assignee="showAssigneeInConversationCard"
                :data-index="unrepliedConversationList.length + index"
                @select-conversation="selectConversation"
                @de-select-conversation="deSelectConversation"
              />
            </Virtualizer>
            <p
              v-if="!allSectionLoading.replied && !repliedSectionCount"
              class="px-3 py-4 text-xs text-center text-n-slate-10"
            >
              {{ $t('CHAT_LIST.SECTIONS.EMPTY') }}
            </p>
            <div
              v-if="allSectionLoading.replied"
              class="flex justify-center my-3"
            >
              <Spinner class="text-n-brand" />
            </div>
            <IntersectionObserver
              v-else-if="!repliedSectionEndReached"
              :options="intersectionObserverOptions"
              @observed="loadMoreAllSection('replied')"
            />
          </template>
        </section>
      </div>
      <Virtualizer
        v-else
        ref="virtualListRef"
        v-slot="{ item, index }"
        :data="conversationList"
      >
        <ConversationItem
          :source="item"
          :label="label"
          :topic-id="topicId"
          :team-id="teamId"
          :folders-id="foldersId"
          :company="company"
          :search-term="searchQuery"
          :conversation-type="conversationType"
          :show-assignee="showAssigneeInConversationCard"
          :data-index="index"
          @select-conversation="selectConversation"
          @de-select-conversation="deSelectConversation"
        />
      </Virtualizer>
      <template v-if="!isPartitionedAllView">
        <div
          v-if="chatListLoading && !searchQuery"
          class="flex justify-center my-4"
        >
          <Spinner class="text-n-brand" />
        </div>
        <p
          v-else-if="showEndOfListMessage"
          class="p-4 text-center text-n-slate-11"
        >
          {{ $t('CHAT_LIST.EOF') }}
        </p>
        <IntersectionObserver
          v-else
          :options="intersectionObserverOptions"
          @observed="loadMoreConversations"
        />
      </template>
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
